import { logCareerScoutProductButtonTap, logCareerScoutProductImpression } from '../../../../../ace/logging/signals';
import { AILogo, Box, Pressable, ScrollView, Text } from '../../../../../mads';
import { useI18n } from '../../../../../shared/I18n';
import {
    encodeViewjobStarterSourceContext,
    getConversationForStarter,
    getConversationForViewjob,
    setActiveViewjobJobKey,
} from '../../../../../shared/lib/conversationStarter/ConversationStarterStorage';
import { JSTModalScreen } from '../../../../../shared/native-modules/tare/JSTModalScreen';
import { openModal } from '../../../../../shared/native-modules/tare/openModal';
import { useRunOnceAfterMount } from '../../../../../shared/util/useRunOnceAfterMount';
import { useViewjobOneGraphModel } from '../../../context/ViewJobDataContext';

import { colors, spacing } from '@indeed/ifl-tokens/react-native';
import React, { useCallback } from 'react';
import { StyleSheet, type LayoutChangeEvent } from 'react-native';

interface StarterChip {
    /** Visible chip text (i18n key). */
    label: string;
    /** Text sent to Career Scout when the chip is tapped (i18n key). Falls
     *  back to `label` when omitted. */
    message?: string;
}

const CS_STARTER_CHIPS: StarterChip[] = [
    { label: 'What would I be doing?' },
    { label: 'What experience do I need?' },
    // Open-ended chip — the label is a question, but we send a flat
    // instruction so the assistant treats it as "the user is unsure what to
    // ask, surface options".
    { label: 'Ask something else?', message: 'Ask something else' },
];

interface AskCareerScoutStartersProps {
    compact?: boolean;
    onLayout?: (event: LayoutChangeEvent) => void;
}

export const AskCareerScoutStarters: React.FC<AskCareerScoutStartersProps> = ({
    compact = false,
    onLayout,
}) => {
    const gt = useI18n();
    const ogModel = useViewjobOneGraphModel();
    const jobKey = ogModel?.job.jobKey;
    const jobTitle = ogModel?.job.jobTitle;
    const companyName = ogModel?.employer?.companyName;

    const handlePress = useCallback(
        (label: string, message: string) => {
            // All chips share a single conversation per jobKey: open the existing
            // thread if any, otherwise create a new one.
            const existingConversationId = jobKey ? getConversationForViewjob(jobKey) : null;

            if (jobKey) {
                setActiveViewjobJobKey(jobKey);
            }

            // Don't re-send a starter message that's already in the thread's
            // history. `getConversationForStarter` doubles as a "this starter
            // has been used in this job's conversation" marker.
            const starterAlreadySent =
                jobKey ? getConversationForStarter(message, jobKey) !== null : false;
            const starterMessage = starterAlreadySent ? null : message;

            const sourceContext = jobKey
                ? encodeViewjobStarterSourceContext(jobKey, message, {
                    jobTitle,
                    companyName,
                })
                : 'viewjob';

            logCareerScoutProductButtonTap({
                elementName: 'askCareerScout',
                assistantMessageId: null,
                from: 'viewJob',
                jobKey: jobKey ?? undefined,
                suggestion: label,
            });

            void openModal(
                JSTModalScreen.aceConversation({
                    starterMessage,
                    conversationId: existingConversationId,
                    sourceContext,
                }),
                'viewjob',
            );
        },
        [jobKey, jobTitle, companyName],
    );

    useRunOnceAfterMount(() => {
        logCareerScoutProductImpression({
            elementName: 'askCareerScout',
            assistantMessageId: null,
            from: 'viewJob',
            jobKey: jobKey ?? undefined,
        });
    });

    const renderChip = (chip: StarterChip) => {
        const label = gt.gettext(chip.label);
        const message = gt.gettext(chip.message ?? chip.label);
        return (
            <Pressable
                key={chip.label}
                onPress={() => handlePress(label, message)}
                sx={[styles.chip, compact && styles.chipCompact]}
                accessibilityRole="button"
                accessibilityLabel={label}
            >
                <Box sx={styles.chipContent}>
                    <AILogo size={compact ? 16 : 'sm'} />
                    <Text level={compact ? 1 : 2} sx={styles.chipText}>
                        {label}
                    </Text>
                </Box>
            </Pressable>
        );
    };

    return (
        <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            onLayout={onLayout}
            contentContainerSx={styles.scrollContent}
        >
            {CS_STARTER_CHIPS.map(renderChip)}
        </ScrollView>
    );
};

export const CS_STARTER_QUESTIONS = CS_STARTER_CHIPS.map(
    (chip) => chip.message ?? chip.label,
);

const styles = StyleSheet.create({
    scrollContent: {
        flexDirection: 'row',
        gap: spacing.s2,
    },
    chip: {
        borderWidth: 1,
        borderColor: colors.borderWeak,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 6,
        paddingHorizontal: spacing.s4,
        paddingVertical: spacing.s2,
        minHeight: 42,
        backgroundColor: colors.background,
        maxWidth: 338,
        justifyContent: 'center',
    },
    chipCompact: {
        paddingHorizontal: spacing.s3,
    },
    chipContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.s2,
    },
    chipText: {
        color: colors.foreground,
    },
});
